import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';
import { cn } from '../../lib/utils';
import { PersonName } from '../shared/PersonName';

interface Option {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SearchableSelectProps {
  value?: string;
  onValueChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Se true, permette di usare il testo digitato come valore libero
   *  quando non corrisponde a nessuna opzione (voce "Usa \"...\""). */
  allowCustomValue?: boolean;
  /** Classi extra per il bottone trigger (merge via cn). */
  triggerClassName?: string;
  /**
   * Nome accessibile del controllo, inoltrato come `aria-label` al bottone trigger.
   * Serve dove la label visibile della riga non e' un `<label htmlFor>` collegato
   * (es. le `PropRow` del drawer condiviso Nota/Attivita): senza questo il trigger
   * si annuncia solo col valore selezionato.
   */
  ariaLabel?: string;
  /** Voci = nomi di persone: se non ci stanno si accorciano dall'inizio e resta visibile la coda (vedi PersonName). */
  personNames?: boolean;
}

export function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = "Seleziona...",
  disabled = false,
  className,
  allowCustomValue = false,
  triggerClassName,
  ariaLabel,
  personNames = false
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  // Vicino al fondo (drawer/modale) il dropdown verso il basso viene tagliato:
  // se sotto non c'è spazio e sopra ce n'è di più, apri verso l'alto
  const [openUp, setOpenUp] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const openDropdown = () => {
    if (selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect();
      // Il taglio non lo fa il viewport ma il primo antenato scrollabile
      // (es. il corpo del drawer, che finisce sopra il footer)
      let clipBottom = window.innerHeight;
      let clipTop = 0;
      let el: HTMLElement | null = selectRef.current.parentElement;
      while (el) {
        const overflowY = getComputedStyle(el).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden') {
          const r = el.getBoundingClientRect();
          clipBottom = Math.min(clipBottom, r.bottom);
          clipTop = Math.max(clipTop, r.top);
          break;
        }
        el = el.parentElement;
      }
      const spaceBelow = clipBottom - rect.bottom;
      const spaceAbove = rect.top - clipTop;
      setOpenUp(spaceBelow < 340 && spaceAbove > spaceBelow);
    }
    setIsOpen(true);
  };

  // Filter options based on search
  useEffect(() => {
    if (!search) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option =>
        option.label.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
    // Reset highlighted index when options change
    setHighlightedIndex(0);
  }, [search, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const selectedOption = options.find(option => option.value === value);
  // Con allowCustomValue mostra il valore libero anche se non è tra le opzioni
  const displayLabel = selectedOption
    ? selectedOption.label
    : (allowCustomValue && value ? value : undefined);

  // Voce "Usa \"<testo>\"": visibile solo se il testo cercato non matcha
  // esattamente nessuna opzione (per label o value)
  const trimmedSearch = search.trim();
  const showCustomOption = allowCustomValue && trimmedSearch.length > 0 &&
    !options.some(option =>
      option.label.toLowerCase() === trimmedSearch.toLowerCase() ||
      option.value.toLowerCase() === trimmedSearch.toLowerCase()
    );
  const totalOptions = filteredOptions.length + (showCustomOption ? 1 : 0);

  const handleSelect = (optionValue: string) => {
    onValueChange(optionValue);
    setIsOpen(false);
    setSearch('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearch('');
      setHighlightedIndex(0);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        handleSelect(filteredOptions[highlightedIndex].value);
      } else if (showCustomOption && highlightedIndex === filteredOptions.length) {
        handleSelect(trimmedSearch);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) openDropdown();
      setHighlightedIndex(prev =>
        prev < totalOptions - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev =>
        prev > 0 ? prev - 1 : totalOptions - 1
      );
    }
  };

  return (
    <div ref={selectRef} className={cn("relative", className)}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => { if (!disabled) { isOpen ? setIsOpen(false) : openDropdown(); } }}
        disabled={disabled}
        aria-label={ariaLabel}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          isOpen && "ring-2 ring-ring ring-offset-2",
          triggerClassName
        )}
      >
        {personNames && displayLabel ? (
          <PersonName name={displayLabel} />
        ) : (
          <span className={cn(
            "truncate",
            !displayLabel && "text-muted-foreground"
          )}>
            {displayLabel ?? placeholder}
          </span>
        )}
        <ChevronDown className={cn(
          "h-4 w-4 opacity-50 transition-transform",
          isOpen && "rotate-180"
        )} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className={cn(
          "absolute z-[10001] min-w-full w-max max-w-[280px] rounded-md border bg-white shadow-lg searchable-select-dropdown",
          openUp ? "bottom-full mb-1" : "top-full mt-1"
        )}>
          {/* Search Input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Cerca..."
                className="w-full h-8 pl-8 pr-2 text-sm border rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Options */}
          <div className="max-h-60 overflow-auto">
            {totalOptions === 0 ? (
              <div className="p-2 text-sm text-gray-500 text-center">
                Nessun risultato trovato
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => !option.disabled && handleSelect(option.value)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  disabled={option.disabled}
                  className={cn(
                    "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none disabled:pointer-events-none disabled:opacity-50",
                    value === option.value && "bg-blue-50 text-blue-600",
                    index === highlightedIndex && "bg-gray-100",
                    "hover:bg-gray-100 focus:bg-gray-100"
                  )}
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    {value === option.value && (
                      <Check className="h-4 w-4" />
                    )}
                  </span>
                  {personNames ? <PersonName name={option.label} /> : <span className="truncate">{option.label}</span>}
                </button>
              ))
            )}
            {showCustomOption && (
              <button
                type="button"
                onClick={() => handleSelect(trimmedSearch)}
                onMouseEnter={() => setHighlightedIndex(filteredOptions.length)}
                className={cn(
                  "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none",
                  highlightedIndex === filteredOptions.length && "bg-gray-100",
                  "hover:bg-gray-100 focus:bg-gray-100"
                )}
              >
                <span className="truncate">Usa "{trimmedSearch}"</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}